import { StateCreator } from 'zustand';

import { addOrDeleteArrayItem, ddpColumns } from '../utils/ddp';
import { getColumnWidth, totalWidth } from '../utils/table';
import { getAlleKnopenFromRBC } from '../utils/voorraadbeheer';

import { PlankDataStore } from './types/PlankDataStore';
import { PlankTableStoreV2, PlankViews } from './types/PlankTableStore';

const plankTableStateCreator: StateCreator<PlankTableStoreV2 & Partial<PlankDataStore>> = (set, get, __) => ({
  tableView: 'materieel',
  setTableView(tableView: PlankViews) {
    set({ tableView });
  },
  showIndelenPanel: false,
  setShowIndelenPanel(explicit = undefined) {
    set((state) => ({ showIndelenPanel: explicit ?? !state.showIndelenPanel }));
  },
  table: {
    rows: [] as string[],
    columns: [...ddpColumns],
    collapsed: [] as string[],
    totalWidth: totalWidth(ddpColumns),
    columnWidth: getColumnWidth(ddpColumns.length),
  },
  tableRegionaal: {
    rows: [] as string[],
    columns: [...ddpColumns],
    collapsed: [] as string[],
    totalWidth: totalWidth(ddpColumns),
    columnWidth: getColumnWidth(ddpColumns.length),
  },

  tableFilters: {
    materieel: [],
    knopen: [],
  },
  setTableFilterMaterieel(materieel: string[]) {
    set((state) => ({ tableFilters: { ...state.tableFilters, materieel } }));
  },
  setTableFilterKnopen(knopen: string[]) {
    set((state) => ({
      tableFilters: { ...state.tableFilters, knopen },
      tableRegionaal: { ...state.tableRegionaal, rows: knopen },
    }));
  },

  availableMatTypes: (matType: string[]) => {
    set((state) => {
      return {
        table: {
          ...state.table,
          rows: matType,
        },
      };
    });
  },
  toggleColumn: (column: string) =>
    set((state) => {
      const key = state.tableView === 'materieel' ? 'table' : 'tableRegionaal';
      const columns = [
        ...addOrDeleteArrayItem(column, state[key].columns).sort((a, b) => {
          return ddpColumns.indexOf(a) - ddpColumns.indexOf(b);
        }),
      ];
      return {
        [key]: {
          ...state[key],
          columns,
          totalWidth: totalWidth(columns),
          columnWidth: getColumnWidth(columns.length),
        },
      };
    }),

  /** Toggle collapse for 1 or all rows. When collapsed it will show all items in a row.
   *  When 0 rows are manually collapsed, it will open / close all.
   *  When 1 or more rows are manually collapsed, it will close all.
   *
   * @returns
   */
  toggleCollapse: () =>
    set((state) => {
      return {
        ...state,
        table: {
          ...state.table,
          collapsed: state.table.collapsed.length ? [] : state.table.rows,
        },
      };
    }),
  /** Toggle collapse for 1 or all rows. When collapsed it will show all items in a row.
   *  When 0 rows are manually collapsed, it will open / close all.
   *  When 1 or more rows are manually collapsed, it will close all.
   *
   * @returns
   */
  toggleCollapseRegionaal() {
    set((state) => ({
      tableRegionaal: {
        ...state.tableRegionaal,
        collapsed: state.tableRegionaal.collapsed.length ? [] : state.tableRegionaal.rows,
      },
    }));
  },
  toggleCollapseRow: (matType: string) => {
    const included = get().isRowCollapsed(matType);
    set((state) => {
      return {
        ...state,
        table: {
          ...state.table,
          collapsed: included
            ? [...state.table.collapsed.filter((row) => row !== matType)]
            : [...state.table.collapsed, matType],
        },
      };
    });
  },
  toggleCollapseRegionaalRow: (knoop: string) => {
    const included = get().isRegionaalRowCollapsed(knoop);
    set((state) => {
      return {
        ...state,
        tableRegionaal: {
          ...state.tableRegionaal,
          collapsed: included
            ? [...state.tableRegionaal.collapsed.filter((row) => row !== knoop)]
            : [...state.tableRegionaal.collapsed, knoop],
        },
      };
    });
  },
  isRowCollapsed: (matType: string) => {
    return get().table.collapsed.includes(matType);
  },
  isRegionaalRowCollapsed: (knoop: string) => get().tableRegionaal.collapsed.includes(knoop),
  showColumn: (column: string) => {
    const { tableView, table, tableRegionaal } = get();
    return tableView === 'materieel' ? table.columns.includes(column) : tableRegionaal.columns.includes(column);
  },
  showRBC(rbc: string) {
    const knopen = getAlleKnopenFromRBC(rbc);
    return knopen.some((knoop) => get().tableRegionaal.rows.includes(knoop));
  },

  getRows() {
    const { tableView, table, tableRegionaal } = get();
    return tableView === 'materieel' ? table.rows : tableRegionaal.rows;
  },
  getColumns() {
    const { tableView, table, tableRegionaal } = get();
    return tableView === 'materieel' ? table.columns : tableRegionaal.columns;
  },
  getCollapsed() {
    const { tableView, table, tableRegionaal } = get();
    return tableView === 'materieel' ? table.collapsed : tableRegionaal.collapsed;
  },
  getTotalWidth() {
    const { tableView, table, tableRegionaal } = get();
    return tableView === 'materieel' ? table.totalWidth : tableRegionaal.totalWidth;
  },
  getColumnWidth() {
    const { tableView, table, tableRegionaal } = get();
    return tableView === 'materieel' ? table.columnWidth : tableRegionaal.columnWidth;
  },
});
export default plankTableStateCreator;
